var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Vue } from 'vue-property-decorator';
import DataTable from '@/components/data-table/DataTable';
import PageHeader from '@/components/page-header/PageHeader';
import ErrorHandler from '@/support/ErrorHandler';
import { ProjectIncludes, Project } from '@/models/Project';
import CreateProjectDialog from '@/views/Projects/CreateProjectDialog/CreateProjectDialog';
import SubjectLabelSelect from '@/components/SubjectLabelSelect/SubjectLabelSelect.vue';
import AExpandableFilter from '@/components/AExpandableFilter/AExpandableFilter.vue';
import ReadRatingDialog from '@/views/Projects/ReadRatingDialog/ReadRatingDialog.vue';
import EditProjectNameDialog from '@/views/Projects/EditProjectNameDialog/EditProjectNameDialog.vue';
import { ProjectCategory } from '@/models/ProjectCategory';
let Projects = class Projects extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #region @Props
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.$pageTitle = 'Projects';
        this.isCreatingProject = false;
        this.isEditingProject = false;
        this.isReadingRating = false;
        this.selectedProject = null;
        this.projectCategoryId = this.$route.params.id || null;
        this.projectCategory = null;
        this.isLoading = true;
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        await this.fetchProjectCategory();
        this.isLoading = false;
    }
    async fetchProjectCategory() {
        if (!this.projectCategoryId)
            return;
        try {
            this.projectCategory = await new ProjectCategory().find(this.projectCategoryId);
            console.log('this.projectCategory: ', this.projectCategory);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #endregion
    // #region Class methods
    onReadRating(project) {
        this.selectedProject = project;
        this.isReadingRating = true;
    }
    onCloseReadRatingDialog() {
        this.isReadingRating = false;
        this.selectedProject = null;
    }
    onCloseEditProjectDialog() {
        this.isEditingProject = false;
        this.selectedProject = null;
    }
    onProjectUpdated() {
        this.refreshDataTable();
        this.isEditingProject = false;
        this.selectedProject = null;
    }
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    dialogOptions(project) {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('dialogOptions.delete', 1, { item: this.$t('dialogOptions.item.project') }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        if (!project) {
                            return;
                        }
                        project
                            .delete()
                            .then((response) => {
                            if (response) {
                                this.refreshDataTable();
                            }
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
    // #endregion
    // #region Async methods
    async duplicateProject(project) {
        try {
            await project.duplicate();
            this.refreshDataTable();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #endregion
    // #region Getters & Setters
    get breadcrumbs() {
        return [{ name: this.$tc('Goal set categories'), path: '/project-categories' }, { name: this.projectCategory && this.projectCategory.name ? this.projectCategory.name : '' }];
    }
    get tableOptions() {
        const model = new Project().include([ProjectIncludes.CHAT, ProjectIncludes.META, ProjectIncludes.CREATED_BY]);
        const id = this.$route.params.id;
        if (id) {
            model.filter({ project_category_id: id });
        }
        return {
            model,
            name: {
                singular: this.$tc('projects.title', 1),
                plural: this.$tc('projects.title', 2),
            },
            headers: [
                {
                    text: `${this.$t('projects.table.name')}`,
                    value: 'name',
                    action: '/projects/{id}',
                },
                {
                    text: `${this.$t('projects.table.description')}`,
                    value: 'description',
                    slot: 'description',
                },
                {
                    text: `${this.$t('Created by')}`,
                    value: 'created_by',
                    // width: '50%',
                    slot: 'created-by',
                },
                {
                    text: `${this.$t('Evaluation')}`,
                    value: 'rating',
                    // width: '50%',
                    slot: 'rating',
                },
                {
                    text: `${this.$t('projects.table.subjects')}`,
                    value: 'meta',
                    transform: (meta) => meta.subject_count || '-',
                },
            ],
            actions: [
                {
                    type: 'view',
                    label: 'view',
                    icon: 'remove_red_eye',
                    action: '/projects/{id}',
                },
                {
                    type: 'duplicate',
                    label: 'duplicate',
                    icon: 'content_copy',
                    action: (vm, project) => {
                        this.duplicateProject(project);
                    },
                },
                {
                    type: 'edit',
                    label: 'edit',
                    icon: 'edit',
                    action: (vm, project) => {
                        if (!project || !project.id) {
                            return;
                        }
                        this.selectedProject = project;
                        this.isEditingProject = true;
                    },
                },
                {
                    type: 'delete',
                    label: 'delete',
                    icon: 'delete',
                    action: (vm, project) => {
                        this.$store.dispatch('openDialog', this.dialogOptions(project));
                    },
                },
            ],
            filters: [],
        };
    }
};
Projects = __decorate([
    Component({
        components: {
            DataTable,
            PageHeader,
            CreateProjectDialog,
            SubjectLabelSelect,
            AExpandableFilter,
            ReadRatingDialog,
            EditProjectNameDialog,
        },
    })
], Projects);
export default Projects;
